import {
  PreclassTestCategoryType,
  PreclassTestNameType,
  PreclassTestOutcomeType,
  PreclassTestResultType,
  PreclassTestSpecificErrorsType,
} from "../../../shared/types/preclass_check";

import { PreclassCheckSettings } from "./settings";

const EXTENSION_ID = PreclassCheckSettings.EXTENSION_URL.replace(/.*\//, "");

export class ExtensionTest {
  private static hasScriptBeenInjected() {
    return document.querySelector("script[src*='minerva-system-check-extension.js']") !== null;
  }

  private static testForExtension(callback: (success: boolean) => void) {
    window.flightLogger.log("[ExtensionCheck] runExtensionTest: testForExtension");
    if (ExtensionTest.hasScriptBeenInjected()) {
      callback(true);
    } else {
      //  if the extension is not available sendMessage will not be defined
      const chrome = (
        window as unknown as {
          chrome?: {
            runtime: {
              sendMessage: (
                extensionId: string,
                message: { type: string },
                callback: () => void,
              ) => void;
              lastError?: { message: string };
            };
          };
        }
      ).chrome;
      const hasSendMessage = chrome && chrome.runtime && chrome.runtime.sendMessage;
      if (hasSendMessage) {
        chrome.runtime.sendMessage(EXTENSION_ID, { type: "ping" }, () => {
          //  there is an extension present but it's not working properly
          if (chrome.runtime.lastError) {
            callback(false);
          } else {
            callback(true);
          }
        });
      } else {
        callback(false);
      }
    }
  }

  public static async testExtension(): Promise<PreclassTestResultType> {
    window.flightLogger.log("[ExtensionCheck] runExtensionTest: testExtension");

    return new Promise((resolve) => {
      /* Calls `callback` with whether the extension is installed. */

      ExtensionTest.testForExtension((success) => {
        window.flightLogger.log(`[ExtensionCheck] In _testForExtension: success=${success}`);
        if (success) {
          if (!ExtensionTest.hasScriptBeenInjected()) {
            // If the extension was installed when the page was originally loaded, it will
            // automatically inject the content script. If it is installed after the page is
            // loaded, we need to ask the background script to inject the content script.
            window.flightLogger.log(
              "[ExtensionCheck] In _testForExtension: Asking extension to inject code",
            );
            const chromeRuntime = (
              window as unknown as {
                chrome?: {
                  runtime: {
                    sendMessage: (
                      extensionId: string,
                      message: { type: string },
                      callback: () => void,
                    ) => void;
                    lastError?: { message: string };
                  };
                };
              }
            ).chrome?.runtime;
            if (chromeRuntime) {
              chromeRuntime.sendMessage(EXTENSION_ID, { type: "inject" }, () => {
                if (chromeRuntime.lastError) {
                  // Should never get here, unless the extension is suddenly unavailable:
                  window.flightLogger.error(
                    "[ExtensionCheck] Error injecting extension: ",
                    chromeRuntime.lastError,
                  );
                  resolve({
                    name: PreclassTestNameType.EXTENSION,
                    category: PreclassTestCategoryType.SOFTWARE,
                    outcome: PreclassTestOutcomeType.FAIL,
                    error: PreclassTestSpecificErrorsType.EXTENSION_FAILED_INJECT,
                  });
                } else {
                  resolve({
                    name: PreclassTestNameType.EXTENSION,
                    category: PreclassTestCategoryType.SOFTWARE,
                    outcome: PreclassTestOutcomeType.SUCCESS,
                  });
                }
              });
            } else {
              // chromeRuntime should always be available here since testForExtension
              // succeeded via sendMessage, but resolve defensively to avoid hanging callers.
              window.flightLogger.error(
                "[ExtensionCheck] Unable to inject extension: chrome.runtime is unavailable",
              );
              resolve({
                name: PreclassTestNameType.EXTENSION,
                category: PreclassTestCategoryType.SOFTWARE,
                outcome: PreclassTestOutcomeType.FAIL,
                error: PreclassTestSpecificErrorsType.EXTENSION_FAILED_INJECT,
              });
            }
          } else {
            resolve({
              name: PreclassTestNameType.EXTENSION,
              category: PreclassTestCategoryType.SOFTWARE,
              outcome: PreclassTestOutcomeType.SUCCESS,
            });
          }
        } else {
          // Check error - failed
          resolve({
            name: PreclassTestNameType.EXTENSION,
            category: PreclassTestCategoryType.SOFTWARE,
            outcome: PreclassTestOutcomeType.FAIL,
            error: PreclassTestSpecificErrorsType.EXTENSION_NOT_INSTALLED,
          });
        }
      });
    });
  }
}
